// FormContainer.jsx
import React, { useEffect } from 'react';

import { Box, Button, Divider, Grid } from '@sunwisesoftware/sunwise-ui';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import {
    ReactHookFormInput,
    ReactHookFormSelect,
    ReactHookFormSelect2,
} from 'common/components/form/RHF';
import ShowErrors from 'common/components/ShowErrors';
import yupResolver from 'common/utils/yupResolver';

import * as actions from '../actions';
import {
    getOptionsToSelectForRHFArray,
    getTemplatesToSelect,
} from '../helpers';
import * as selectors from '../selectors';
import validate from '../validate';

import ReactHookFormArray from './ReactHookFormArray';

const FormContainer = ({
    countries,
    errors: serverErrors,
    filterData,
    handleClickCloseForm,
    handleClickSave,
    initialize,
    initialValues,
    isFetchingItem,
    isInitializing,
    isNewRecord,
    isSaving,
    itemData,
    products,
    roofTypesToSelect,
    templates,
}) => {
    const { t } = useTranslation();
    const {
        control,
        getValues,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: initialValues,
        resolver: yupResolver(validate),
    });

    useEffect(() => reset(initialValues), [initialValues]);

    useEffect(() => {
        initialize();
    }, []);

    const handleOnSave = (values) => handleClickSave(filterData, values);

    const isDisabledCatalogFields = isFetchingItem || isInitializing;

    return (
        <form>
            <Box sx={{ mb: 2 }}>
                <ReactHookFormInput
                    control={control}
                    name="name"
                    label={t('Name')}
                    placeholder={t('Name')}
                />
            </Box>

            <Box sx={{ mb: 2 }}>
                <ReactHookFormSelect
                    control={control}
                    disabled={isDisabledCatalogFields}
                    label={t('Rooftop type')}
                    name="type_application"
                    options={roofTypesToSelect}
                />
            </Box>

            <Box sx={{ mb: 2 }}>
                <ReactHookFormSelect
                    control={control}
                    disabled={isDisabledCatalogFields}
                    label={t('Country')}
                    name="country"
                    options={countries}
                />
            </Box>

            <Box sx={{ mb: 2 }}>
                <ReactHookFormSelect
                    control={control}
                    disabled={isDisabledCatalogFields}
                    label={t('Template BOM')}
                    name="template_bom"
                    options={getTemplatesToSelect(
                        templates,
                        isNewRecord,
                        itemData,
                        'bom',
                    )}
                />
            </Box>

            <Box sx={{ mb: 2 }}>
                <ReactHookFormSelect2
                    control={control}
                    disabled={isDisabledCatalogFields}
                    label={t('Template system')}
                    name="template_system"
                    options={[
                        {
                            label: '',
                            options: getTemplatesToSelect(
                                templates,
                                isNewRecord,
                                itemData,
                                'system',
                            ),
                        },
                    ]}
                />
            </Box>

            <Divider sx={{ mb: 2 }} />

            <ReactHookFormArray
                control={control}
                disabled={isDisabledCatalogFields}
                dot="#f9c109"
                errors={errors}
                getValues={getValues}
                groupName="products"
                itemsToSelect={getOptionsToSelectForRHFArray({
                    data: products,
                    isNewRecord,
                    label: t('Select products'),
                    itemData,
                    prefix: 'products',
                })}
                label={t('Product', { count: 2 })}
            />

            <ShowErrors errors={serverErrors} />

            <Grid container mt={2}>
                <Grid
                    size="grow"
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        justifyContent: { xs: 'center', md: 'right' },
                    }}
                >
                    <Button
                        color="secondary"
                        disabled={isSaving}
                        onClick={handleClickCloseForm}
                        sx={{
                            width: { xs: '100%', md: 'auto' },
                            order: { xs: 2, md: 1 },
                            mr: { xs: 0, md: 2 },
                        }}
                        variant="text"
                    >
                        {t('Cancel')}
                    </Button>

                    <Button
                        disabled={isSaving}
                        onClick={handleSubmit(handleOnSave)}
                        sx={{
                            width: { xs: '100%', md: 'auto' },
                            order: { xs: 1, md: 2 },
                            mb: { xs: 2, md: 0 },
                        }}
                        type="button"
                        variant="outlined"
                    >
                        {t('Save')}
                    </Button>
                </Grid>
            </Grid>
        </form>
    );
};

const mapStateToProps = createStructuredSelector({
    countries: selectors.getCountriesToSelect,
    errors: selectors.getSaveItemErrors,
    initialValues: selectors.getInitialValues,
    isFetchingItem: selectors.getIsFetchingItem,
    isInitializing: selectors.getIsInitializing,
    isNewRecord: selectors.getIsNewRecord,
    isSaving: selectors.getIsSavingItem,
    itemData: selectors.getFetchItemData,
    products: selectors.getFetchProductsData,
    roofTypesToSelect: selectors.getRoofTypesToSelect,
    templates: selectors.getFetchTemplatesData,
});

const mapDispatchToProps = (dispatch) => ({
    handleClickDelete: () => dispatch(actions.prepareDelete()),
    handleClickSave: (filterData, values) =>
        dispatch(actions.save(filterData, values)),
    initialize: (branchOffice) =>
        dispatch(actions.initialFetching(branchOffice)),
});

FormContainer.propTypes = {
    countries: PropTypes.array,
    errors: PropTypes.array,
    filterData: PropTypes.object,
    handleClickCloseForm: PropTypes.func,
    handleClickSave: PropTypes.func,
    initialize: PropTypes.func,
    initialValues: PropTypes.object,
    isFetchingItem: PropTypes.bool,
    isInitializing: PropTypes.bool,
    isNewRecord: PropTypes.bool,
    isSaving: PropTypes.bool,
    itemData: PropTypes.object,
    products: PropTypes.array,
    roofTypesToSelect: PropTypes.array,
    templates: PropTypes.array,
};

export default connect(mapStateToProps, mapDispatchToProps)(FormContainer);
